import { Typography } from '@mui/material';
import { BarPlot } from '@mui/x-charts/BarChart';
import { ChartContainer } from '@mui/x-charts/ChartContainer';
import { ChartsGrid } from '@mui/x-charts/ChartsGrid';
import { ChartsTooltip } from '@mui/x-charts/ChartsTooltip';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { LinePlot } from '@mui/x-charts/LineChart';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { hexToRGBA } from '../BarChart';
import EnhacedBox from '../Box';

interface CombinedChartProps {
  data: Record<string, { quantity: number; amount: number }>;
  title?: string;
  icon?: React.ReactNode;
  primaryColor: string;
  secondaryColor: string;
  height?: number;
  xAxisLabel?: string;
  yAxisLabel?: string;
  rightYAxisLabel?: string;
  sortBy?: 'asc' | 'desc';
  customMargin?: {
    left?: number;
    right?: number;
    top?: number;
    bottom?: number;
  };
  customQuantityValueFormatter?: (value: number | null) => string;
  customAmountValueFormatter?: (value: number | null) => string;
  customYAxisValueFormatter?: (value: number) => string;
  customRightYAxisValueFormatter?: (value: number) => string;
}

const CombinedChart = ({
  data,
  title,
  icon,
  primaryColor,
  secondaryColor,
  height = 200,
  xAxisLabel,
  yAxisLabel,
  rightYAxisLabel,
  sortBy = 'asc',
  customMargin = { left: 40, right: 40, top: 20, bottom: 20 },
  customQuantityValueFormatter,
  customAmountValueFormatter,
  customYAxisValueFormatter,
  customRightYAxisValueFormatter,
}: CombinedChartProps) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const [chartHeight, setChartHeight] = useState(height);
  const [chartWidth, setChartWidth] = useState(0);

  // Default value formatters
  const quantityFormatter = (value: number | null) => {
    if (value === null) return '';
    return customQuantityValueFormatter
      ? customQuantityValueFormatter(value)
      : value.toString();
  };

  const amountFormatter = (value: number | null) => {
    if (value === null) return '';
    return customAmountValueFormatter
      ? customAmountValueFormatter(value)
      : value.toString();
  };

  // Default axis value formatters
  const yAxisFormatter = (value: number) => {
    return customYAxisValueFormatter
      ? customYAxisValueFormatter(value)
      : value.toString();
  };

  const rightYAxisFormatter = (value: number) => {
    return customRightYAxisValueFormatter
      ? customRightYAxisValueFormatter(value)
      : value.toString();
  };

  const processedData = useMemo(() => {
    if (!data) return [];

    const entries = Object.entries(data);

    return entries.map(([label, values]) => ({
      category: label,
      quantity: values.quantity,
      amount: values.amount,
    }));
  }, [data, sortBy]);

  const series = [
    {
      type: 'bar' as const,
      dataKey: 'quantity',
      color: primaryColor,
      yAxisId: 'leftAxis',
      valueFormatter: quantityFormatter,
    },
    {
      type: 'line' as const,
      dataKey: 'amount',
      color: secondaryColor,
      yAxisId: 'rightAxis',
      valueFormatter: amountFormatter,
    },
  ];

  useEffect(() => {
    const updateDimensions = () => {
      if (elementRef.current) {
        const { width } = elementRef.current.getBoundingClientRect();
        setChartWidth(width - 40);
        setChartHeight(width * 0.3);
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);

    return () => window.removeEventListener('resize', updateDimensions);
  }, [height]);

  return (
    <EnhacedBox small ref={elementRef}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 16,
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: hexToRGBA(primaryColor, 0.12),
          }}
        >
          {icon}
        </div>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            fontSize: '1.1rem',
          }}
        >
          {title}
        </Typography>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <ChartContainer
          series={series}
          xAxis={[
            {
              scaleType: 'band',
              dataKey: 'category',
              label: xAxisLabel,
              tickLabelStyle: {
                fontSize: 12,
                angle: -45,
                textAnchor: 'end',
              },
            },
          ]}
          yAxis={[
            {
              id: 'leftAxis',
              label: yAxisLabel,
              labelStyle: {
                fontSize: 12,
                marginRight: 16,
              },
              position: 'left',
              tickLabelStyle: {
                fontSize: 11,
              },
              valueFormatter: yAxisFormatter,
            },
            {
              id: 'rightAxis',
              position: 'right',
              label: rightYAxisLabel,
              labelStyle: {
                fontSize: 12,
                marginLeft: 16,
              },
              tickLabelStyle: {
                fontSize: 11,
              },
              valueFormatter: rightYAxisFormatter,
            },
          ]}
          dataset={processedData}
          height={chartHeight}
          width={chartWidth}
          margin={customMargin}
        >
          <ChartsGrid horizontal />
          <BarPlot />
          <LinePlot />

          <ChartsXAxis />
          <ChartsYAxis axisId="leftAxis" />
          <ChartsYAxis axisId="rightAxis" />
          <ChartsTooltip />
        </ChartContainer>
      </div>
    </EnhacedBox>
  );
};

export default CombinedChart;
